import React, { useEffect } from "react";
import { useMediaQuery } from "react-responsive";

import { Container } from "@mui/system";
import { useParams } from "react-router-dom";
import {
  getColorWithMode,
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../constants/colors";
import Stack from "@mui/material/Stack";
import OverallSummaryRow from "./OverallSummaryRow";
import SideContainer from "./SideContainer";
import ShareTableContainer from "./ShareTableContainer";
import {
  getSharedPackWithId,
  getUserIsLoggedIn,
  getWeightInGrams,
  getFormattedWeightInUnit,
  handleUserPackVote,
  convertWeightInUnitToNewUnit,
} from "../utils";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import SkeletonPack from "./SkeletonPack";
import PackDescription from "./PackDescription";
import PackChecklistContainer from "./PackChecklistContainer";
import KebabMenu from "./KebabMenu";
import { Helmet } from "react-helmet";
import {
  Calendar,
  CircleUserRound,
  MapPin,
  ThumbsDown,
  ThumbsUp,
  Timer,
} from "lucide-react";
import { DURATION_SETTINGS, SEASON_SETTINGS } from "../constants/packSettings";
import ShareSharedPackDialog from "./ShareSharedPackDialog";
import momenttz from "moment-timezone";
import { exportToCSV } from "../utils/pack";
import SocialLinks from "./SocialLinks";
import { generatePackMetadata } from "../utils/sanitize";

const formatDate = (date) => {
  const timeZoneString = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // subtract date because server returns time since update
  const updatedAt = Date.now() - date;
  return momenttz(updatedAt).tz(timeZoneString).format("MMM DD, YYYY");
};

const getDisplayChips = (tags, isMobile) => {
  const { duration, season, trail } = tags;
  return [
    trail && trail.length ? (
      <Chip
        key={trail}
        icon={<MapPin size={14} />}
        color="info"
        variant="outlined"
        size={isMobile ? "large" : "small"}
        label={trail}
        sx={{ maxWidth: "180px", overflowX: "ellipsis", whitespace: "nowrap" }}
      />
    ) : null,
    season && season.length ? (
      <Chip
        key={season}
        icon={<Calendar size={14} />}
        color="warning"
        variant="outlined"
        size={isMobile ? "large" : "small"}
        label={SEASON_SETTINGS[season]}
      />
    ) : null,
    duration && duration.length ? (
      <Chip
        key={duration}
        icon={<Timer size={14} />}
        color="secondary"
        variant="outlined"
        size={isMobile ? "large" : "small"}
        label={DURATION_SETTINGS[duration]}
      />
    ) : null,
  ].filter((chip) => chip);
};

const hasTags = (tags) => {
  const filteredTags = Object.values(tags).filter((tag) => tag);
  return filteredTags.length > 0;
};

// ItemList + BreadcrumbList structured data so shared gear lists are
// eligible for richer search results.
const buildPackStructuredData = (packName, tableData, id) => {
  const items = [];
  tableData.forEach((category) => {
    Object.values(category.rows || {}).forEach((row) => {
      if (row.item) items.push(row.item);
    });
  });

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: packName,
    url: `https://www.packwizard.com/s/${id}`,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 50).map((name, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name,
    })),
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Browse Packs",
        item: "https://www.packwizard.com/browse",
      },
      { "@type": "ListItem", position: 2, name: packName },
    ],
  };

  return { itemList, breadcrumbs };
};

const getGenericChips = (tags, isMobile) => {
  const { tag0, tag1, tag2 } = tags;
  return [tag0, tag1, tag2]
    .map((tag) => {
      if (tag) {
        return (
          <Chip
            key={tag}
            size={isMobile ? "large" : "small"}
            color="success"
            variant="outlined"
            label={tag}
          />
        );
      } else {
        return null;
      }
    })
    .filter((chip) => chip);
};

const getDefaultTags = (userTags = []) => {
  const tagMap = userTags.reduce(
    (acc, { type, value }) => {
      acc[type] = value;
      return acc;
    },
    {
      duration: "",
      season: "",
      trail: "",
      tag0: "",
      tag1: "",
      tag2: "",
      hidePrices: false,
    }
  );
  return tagMap;
};

const ShareContainer = ({
  setMainAlertDetails,
  setMainAlertIsOpen,
  isDarkMode,
  handleSelectNewPack,
}) => {
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 1380 });
  const isMobile = useMediaQuery({ maxWidth: 898 });

  const { id } = useParams();
  const [packName, setPackName] = React.useState("");
  const [tableId, setTableId] = React.useState(null);
  const [packDescription, setPackDescription] = React.useState("");
  const [tableData, setTableData] = React.useState([]);
  const [unit, setUnit] = React.useState("lb");
  const [hasError, setHasError] = React.useState(false);
  const [packVisualizationToggleState, setPackVisualizationToggleState] =
    React.useState(false);
  const [packIsLoading, setPackIsLoading] = React.useState(true);
  const [tags, setTags] = React.useState({});
  const [voteCount, setVoteCount] = React.useState(1);
  const [packIsUpvoted, setPackIsUpvoted] = React.useState(false);
  const [packIsDownvoted, setPackIsDownvoted] = React.useState(false);
  const [affiliateLinksAreOff, setAffiliateLinksAreOff] = React.useState(false);
  const [shareSharedPackDialogIsOpen, setShareSharedPackDialogIsOpen] =
    React.useState(false);
  const [shareableId, setShareableId] = React.useState("");
  const [sideNavIsOpen, setSideNavIsOpen] = React.useState(true);
  const [packOwner, setPackOwner] = React.useState(true);
  const [lastUpdated, setLastUpdated] = React.useState(null);
  const [isViewedAsChecklist, setIsViewedAsChecklist] = React.useState(false);
  const [overridingUnit, setOverridingUnit] = React.useState(null);

  // Pack-level flag: show an image column in every category table if any
  // item in the pack has an image, keeping the tables' layouts consistent.
  const packHasImages = React.useMemo(
    () =>
      tableData.some((table) =>
        Object.values(table.rows || {}).some(
          (row) =>
            row &&
            !["ADD_ROW", "ADDING_ROW", "DIVIDER"].includes(row.type) &&
            typeof row.imageUrl === "string" &&
            row.imageUrl.trim().length > 0
        )
      ),
    [tableData]
  );
  const [currency, setCurrency] = React.useState("USD");
  const [socials, setSocials] = React.useState({});

  useEffect(() => {
    (async () => {
      const packData = await getSharedPackWithId(id);
      if (packData.error) {
        setHasError(true);
      } else {
        if (packData.packUnit) setUnit(packData.packUnit);
        setPackName(packData.packName);
        setTableData(packData.tableData);
        setPackDescription(packData.packDescription);
        setTableId(packData.tableId);
        setTags(getDefaultTags(packData.tags));
        setVoteCount(packData.voteCount || 0);
        const userUpvoteList = packData.userUpvotes || [];
        const userDownvoteList = packData.userDownvotes || [];
        setPackIsUpvoted(userUpvoteList.includes(packData.tableId));
        setPackIsDownvoted(userDownvoteList.includes(packData.tableId));
        setAffiliateLinksAreOff(packData.hasAffiliateLinksTurnedOff || false);
        setShareableId(packData.shareableId);
        setPackOwner(packData.username);
        setSocials(packData.socials);
        setLastUpdated(packData.lastUpdated);
        setCurrency(packData.packCurrency || "USD");
      }
      setPackIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    const newTableData = tableData.map((data) => ({
      ...data,
      summaryRowUnit: unit,
    }));
    setTableData(newTableData);
  }, [unit]);

  const { weightInGrams, price } = tableData.reduce(
    (acc, curr) => {
      const { summedRowWeight, summedRowPrice } = Object.values(
        curr.rows
      ).reduce(
        (acc, curr) => {
          return {
            summedRowWeight:
              acc.summedRowWeight +
              getWeightInGrams(curr.weight, curr.unit) * curr.quantity,
            summedRowPrice: acc.summedRowPrice + curr.price * curr.quantity,
          };
        },
        { summedRowWeight: 0, summedRowPrice: 0 }
      );
      return {
        weightInGrams: acc.weightInGrams + summedRowWeight,
        price: acc.price + summedRowPrice,
      };
    },
    {
      weightInGrams: 0,
      price: 0,
    }
  );

  const handleSetPackToImperial = () => {
    setOverridingUnit("IMPERIAL");
    setUnit("lb");
  };

  const handleSetPackToMetric = () => {
    setOverridingUnit("METRIC");
    setUnit("kg");
  };

  const handleExportToCSVShared = () => {
    exportToCSV(packName, tableData);
  };

  const handleViewAsChecklist = () => {
    setIsViewedAsChecklist(true);
  };

  const handleLeaveChecklist = () => {
    setIsViewedAsChecklist(false);
  };

  // Shared packs belong to someone else: checklist checkboxes only update
  // local state for this viewing session, never the owner's pack.
  const handleModifyChecklistLocally = (newTableData) => {
    setTableData(newTableData);
  };

  const handleSetTableData = (tableIndex, key, value) => {
    const newData = [...tableData];
    newData[tableIndex][key] = value;
    setTableData(newData);
  };

  const getColorForUpvote = (packIsUpvoted, packIsDownvoted) => {
    if (packIsUpvoted) return isDarkMode ? "#4caf50" : "#1b5e20";
    if (packIsDownvoted) return isDarkMode ? "#ff9800" : "#e65100";
    return "";
  };

  const getUpvoteCount = (count = 0, packIsUpvoted, packIsDownvoted) => {
    let finalCount = count > 1 ? count : 1;
    if (packIsUpvoted) finalCount += 1;
    if (packIsDownvoted) finalCount -= 1;
    return finalCount;
  };

  const handleChangeAllUnits = (newUnit) => {
    const newData = [...tableData];
    newData.forEach((table, index) => {
      Object.keys(table.rows || {}).forEach((rowKey) => {
        const rowData = newData[index].rows[rowKey];
        const oldUnit = rowData.unit;
        const newWeight = convertWeightInUnitToNewUnit(
          rowData.weight,
          oldUnit,
          newUnit
        );
        newData[index].rows[rowKey].unit = newUnit;
        newData[index].rows[rowKey].weight = newWeight;
      });
    });
    setTableData(newData);
  };

  const handleVote = (voteType, packId) => {
    if (voteType === "upvote") {
      if (packIsUpvoted) {
        setPackIsUpvoted(false);
        handleUserPackVote("UPVOTE", packId);
      } else {
        setPackIsUpvoted(true);
        setPackIsDownvoted(false);
        handleUserPackVote("UPVOTE", packId);
      }
    } else if (voteType === "downvote") {
      if (packIsDownvoted) {
        setPackIsDownvoted(false);
        handleUserPackVote("DOWNVOTE", packId);
      } else {
        setPackIsUpvoted(false);
        setPackIsDownvoted(true);
        handleUserPackVote("DOWNVOTE", packId);
      }
    }
  };

  const setShareSharedPackIsOpen = () => {
    setShareSharedPackDialogIsOpen(true);
  };

  const metadata = generatePackMetadata({
    packName,
    packDescription,
    weightInGrams,
    id,
    getFormattedWeightInUnit,
  });

  return (
    <>
      <Helmet>
        <title>{metadata.title}</title>
        <meta property="og:title" content={metadata.ogTitle} />
        <meta property="og:description" content={metadata.ogDescription} />
        <link rel="canonical" href={metadata.canonicalUrl} />
        <meta property="og:image" content={metadata.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.twitterTitle} />
        <meta
          name="twitter:description"
          content={metadata.twitterDescription}
        />
        <meta name="twitter:image" content={metadata.twitterImage} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="description" content={metadata.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={metadata.canonicalUrl} />
        {tableData.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(
              buildPackStructuredData(packName, tableData, id).itemList
            )}
          </script>
        )}
        {tableData.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(
              buildPackStructuredData(packName, tableData, id).breadcrumbs
            )}
          </script>
        )}
      </Helmet>
      <ShareSharedPackDialog
        shareSharedPackDialogIsOpen={shareSharedPackDialogIsOpen}
        setShareSharedPackDialogIsOpen={setShareSharedPackDialogIsOpen}
        shareableLink={shareableId}
      />
      <Container
        fixed
        style={{
          backgroundColor: getColorWithMode(isDarkMode, "base"),
          float: "right",
          width: isDesktopOrLaptop ? "auto" : "100%",
          maxWidth: "100vw",
          minHeight: "920px",
          minWidth: "750px",
          marginLeft: "0px",
          paddingLeft: "0px",
        }}
      >
        {isViewedAsChecklist ? (
          <PackChecklistContainer
            packName={packName}
            tableData={tableData}
            darkMode={isDarkMode}
            handleLeaveChecklist={handleLeaveChecklist}
            modifyPack={handleModifyChecklistLocally}
          />
        ) : (
        <Stack
          justifyContent="space-between"
          alignContent={isMobile ? "center" : ""}
          alignItems={"center"}
          sx={{
            minHeight: "720px",
            width: isDesktopOrLaptop ? "1200px" : "auto",
            maxWidth: "100vw",
            margin: "0 auto",
          }}
        >
          <Stack
            direction="row"
            justifyContent={"space-between"}
            alignItems="center"
            width="100%"
            sx={{ padding: "5px 5px 5px 5px" }}
          >
            <Stack direction="row" alignItems="center">
              {packOwner ? (
                <Chip
                  key={`${packOwner}-chip`}
                  icon={<CircleUserRound size={14} />}
                  variant="outlined"
                  size={isMobile ? "large" : "small"}
                  label={packOwner}
                  onClick={() => {
                    window.location.assign(`/user/${packOwner}`);
                  }}
                />
              ) : (
                <></>
              )}
              {socials ? <SocialLinks {...socials} size="small" /> : <></>}
            </Stack>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: HEADING_FONT,
                color: getMutedText(isDarkMode),
              }}
            >
              {`Last updated: ${formatDate(lastUpdated)}`}
            </Typography>
          </Stack>
          {!sideNavIsOpen ? (
            <Button
              size={isMobile ? "large" : "small"}
              sx={{
                borderRadius: "9px",
                fontFamily: HEADING_FONT,
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={() => {
                setSideNavIsOpen(!sideNavIsOpen);
              }}
            >
              Show Pack Stats & Item Suggestions
            </Button>
          ) : (
            <></>
          )}
          <Box>
            <Collapse in={sideNavIsOpen}>
              <SideContainer
                key={tableId}
                tableData={tableData}
                unit={unit}
                toggleState={packVisualizationToggleState}
                setToggleState={setPackVisualizationToggleState}
                packIsLoading={packIsLoading}
                darkMode={isDarkMode}
                setSideNavIsOpen={setSideNavIsOpen}
              />
            </Collapse>
          </Box>
          <Stack sx={{ width: "100%", marginLeft: "10px" }}>
            {hasError ? (
              <Container sx={{ width: "805px" }}>
                <Typography
                  sx={{
                    fontSize: "20px",
                    textAlign: "center",
                    marginTop: "200px",
                  }}
                >
                  There is no pack associated with this link
                </Typography>
              </Container>
            ) : (
              <>
                <Container
                  fixed
                  sx={{
                    width: "100%",
                    overflowX: "hidden",
                    float: "left",
                    marginLeft: "0px",
                    minHeight: sideNavIsOpen ? "475px" : "785px",
                    maxWidth: "100vw",
                  }}
                >
                  {packIsLoading ? (
                    <SkeletonPack />
                  ) : (
                    <Stack sx={{ width: "100%", maxWidth: "100vw" }}>
                      <Stack
                        direction="row"
                        justifyContent={"space-between"}
                        alignItems="center"
                        sx={{ width: "calc(100% + 10px)" }}
                      >
                        <Typography
                          variant="h1"
                          fontSize={isMobile ? "32px" : "30px"}
                          sx={{
                            fontFamily: HEADING_FONT,
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {packName}
                        </Typography>
                        <Stack direction="row">
                          <Paper
                            elevation={0}
                            sx={{
                              height: isMobile ? "60px" : "52px",
                              borderRadius: "10px",
                              border: `1px solid ${getBorder(isDarkMode)}`,
                              backgroundImage: "none",
                              padding: "2px 6px",
                            }}
                          >
                            <Stack>
                              <Typography
                                textAlign="center"
                                sx={{
                                  fontFamily: HEADING_FONT,
                                  fontWeight: 600,
                                  fontSize: "10px",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.08em",
                                  color: getMutedText(isDarkMode),
                                }}
                              >
                                Rate
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="space-between"
                                sx={{ height: "100%", marginTop: "-4px" }}
                                alignItems="center"
                              >
                                <IconButton
                                  size={isMobile ? "large" : "small"}
                                  disabled={!getUserIsLoggedIn()}
                                  onClick={() => {
                                    handleVote("upvote", tableId);
                                  }}
                                >
                                  <ThumbsUp
                                    size={18}
                                    color={
                                      packIsUpvoted
                                        ? isDarkMode
                                          ? "#4caf50"
                                          : "#1b5e20"
                                        : "currentColor"
                                    }
                                  />
                                </IconButton>
                                <Box
                                  sx={{
                                    fontFamily: HEADING_FONT,
                                    fontWeight: 700,
                                    color: getColorForUpvote(
                                      packIsUpvoted,
                                      packIsDownvoted
                                    ),
                                  }}
                                >
                                  {getUpvoteCount(
                                    voteCount,
                                    packIsUpvoted,
                                    packIsDownvoted
                                  )}
                                </Box>
                                <IconButton
                                  size={isMobile ? "large" : "small"}
                                  disabled={!getUserIsLoggedIn()}
                                  onClick={() => {
                                    handleVote("downvote", tableId);
                                  }}
                                >
                                  <ThumbsDown
                                    size={18}
                                    color={
                                      packIsDownvoted
                                        ? isDarkMode
                                          ? "#ff9800"
                                          : "#e65100"
                                        : "currentColor"
                                    }
                                  />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Paper>
                          <KebabMenu
                            isSharedPack
                            tableId={tableId}
                            setMainAlertDetails={setMainAlertDetails}
                            setMainAlertIsOpen={setMainAlertIsOpen}
                            handleSelectNewPack={handleSelectNewPack}
                            setShareSharedPackIsOpen={setShareSharedPackIsOpen}
                            handleSetPackToMetric={handleSetPackToMetric}
                            handleSetPackToImperial={handleSetPackToImperial}
                            handleExportToCSVShared={handleExportToCSVShared}
                            handleViewAsChecklist={handleViewAsChecklist}
                          />
                        </Stack>
                      </Stack>
                      {hasTags(tags) ? (
                        <Stack
                          direction="row"
                          sx={{
                            marginTop: "5px !important",
                            height: isMobile ? "50px" : "40px",
                          }}
                          spacing={1}
                        >
                          <Stack
                            direction="row"
                            sx={{ width: "100%" }}
                            justifyContent="space-between"
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ maxWidth: "100vw", overflowX: "scroll" }}
                            >
                              {getDisplayChips(tags, isMobile)}
                              {getGenericChips(tags, isMobile)}
                            </Stack>
                          </Stack>
                        </Stack>
                      ) : (
                        <></>
                      )}
                      <PackDescription
                        packDescription={packDescription}
                        shareable
                        darkMode={isDarkMode}
                      />
                      <Stack
                        spacing={2}
                        sx={{ maxWidth: "100vw", marginTop: "5px" }}
                      >
                        {tableData.map((individualTableData, tableIndex) => (
                          <ShareTableContainer
                            overridingUnit={overridingUnit}
                            key={tableIndex}
                            tableData={individualTableData}
                            tableIndex={tableIndex}
                            handleSetTableData={handleSetTableData}
                            isDarkMode={isDarkMode}
                            affiliateLinksAreOff={affiliateLinksAreOff}
                            packCurrency={currency}
                            hidePrices={tags.hidePrices}
                            handleChangeAllUnits={handleChangeAllUnits}
                            showImages={packHasImages}
                          />
                        ))}
                      </Stack>
                    </Stack>
                  )}
                </Container>
                <OverallSummaryRow
                  isShared
                  tableData={tableData}
                  unit={unit}
                  setUnit={setUnit}
                  darkMode={isDarkMode}
                  weightInGrams={weightInGrams}
                  price={price}
                  packCurrency={currency}
                  hidePrices={tags.hidePrices}
                />
              </>
            )}
          </Stack>
        </Stack>
        )}
      </Container>
    </>
  );
};

export default ShareContainer;
